/**
 * Application Configuration
 *
 * This file centralizes all configuration settings for the application.
 * Use these constants instead of hardcoding URLs or settings.
 */

// API Base URL - point to the backend service (Render backend)
// Prefer env var from Render; fallback to the deployed backend domain.
// Prefer custom domain by default; environment variable can override per-deploy
// Default to localhost for development convenience if not specified
export const API_BASE_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5000";

// Media Worker URL - Python FastAPI service for heavy video processing
// In production, this might be the same as API_BASE_URL if proxied, or a separate service
export const MEDIA_API_URL = process.env.REACT_APP_MEDIA_API_URL || "http://localhost:8000";

// Public site URL used for user-facing pages (Terms, Privacy, etc.)
export const PUBLIC_SITE_URL = process.env.REACT_APP_SITE_URL || "https://autopromote.org";

// Feature flags (controlled via environment variables)
// Wolf Hunt is disabled by default; set REACT_APP_ENABLE_WOLF_HUNT=true to enable.
export const ENABLE_WOLF_HUNT = process.env.REACT_APP_ENABLE_WOLF_HUNT === "true";

// API Endpoints
export const API_ENDPOINTS = {
  // Auth endpoints
  LOGIN: `${API_BASE_URL}/api/auth/login`,
  REGISTER: `${API_BASE_URL}/api/auth/register`,
  FORGOT_PASSWORD: `${API_BASE_URL}/api/auth/forgot-password`,
  REQUEST_RESET: `${API_BASE_URL}/api/auth/forgot-password`,
  RESET_PASSWORD: `${API_BASE_URL}/api/auth/reset-password`,
  ADMIN_LOGIN: `${API_BASE_URL}/api/auth/admin-login`,
  VERIFY_TOKEN: `${API_BASE_URL}/api/auth/verify`,

  // Content endpoints
  CONTENT_UPLOAD: `${API_BASE_URL}/api/content/upload`,
  CONTENT_SOURCE_UPLOAD: `${API_BASE_URL}/api/content/upload/source-file`,
  CONTENT_UPLOAD_READINESS: `${API_BASE_URL}/api/content/upload/readiness`,
  MEDIA_PROCESS: `${API_BASE_URL}/api/media/process`,
  MEDIA_EXTRACT_AUDIO: `${API_BASE_URL}/api/media/extract-audio`,
  MEDIA_WORKER_HEALTH: `${API_BASE_URL}/api/media/worker-health`,
  MEDIA_RENDER_CLIP: `${API_BASE_URL}/api/media/render-clip`,
  MEDIA_RENDER_MULTICAM: `${API_BASE_URL}/api/media/render-multicam`,
  MEDIA_STATUS: jobId => `${API_BASE_URL}/api/media/status/${encodeURIComponent(jobId)}`,
  CLIPS_PROMO_SUMMARY: `${API_BASE_URL}/api/clips/promo-summary`,
  CLIPS_PROMO_SUMMARY_ESTIMATE: `${API_BASE_URL}/api/clips/promo-summary/estimate`,
  CLIPS_ANALYSIS: analysisId => `${API_BASE_URL}/api/clips/analysis/${encodeURIComponent(analysisId)}`,
  CLIPS_PROMO_SUMMARY_PREVIEW_STREAM: analysisId => `${API_BASE_URL}/api/clips/promo-summary/${encodeURIComponent(analysisId)}/preview/stream`,
  CLIPS_USER: `${API_BASE_URL}/api/clips/user`,
  MY_CONTENT: `${API_BASE_URL}/api/content/my-content`,
  MY_SCHEDULES: `${API_BASE_URL}/api/content/my-promotion-schedules`,
  REPOST_ACTIVITY: `${API_BASE_URL}/api/content/repost-activity`,
  REPOST_PREVIEW: id => `${API_BASE_URL}/api/content/${encodeURIComponent(id)}/repost-preview`,
  CONTENT_DIAGNOSIS: id => `${API_BASE_URL}/api/content/${encodeURIComponent(id)}/diagnosis`,
  CONTENT_DIAGNOSIS_REMEDIATE: id =>
    `${API_BASE_URL}/api/content/${encodeURIComponent(id)}/diagnosis/remediate`,
  CONTENT_DIAGNOSIS_HISTORY: id =>
    `${API_BASE_URL}/api/content/${encodeURIComponent(id)}/diagnosis/history`,
  CONTENT_DIAGNOSIS_POLICY: id =>
    `${API_BASE_URL}/api/content/${encodeURIComponent(id)}/diagnosis/policy`,
  CONTENT_DIAGNOSIS_RUN_AUTO: id =>
    `${API_BASE_URL}/api/content/${encodeURIComponent(id)}/diagnosis/run-auto`,

  // User endpoints
  USERS_ME: `${API_BASE_URL}/api/users/me`,
  USERS_NOTIFICATIONS: `${API_BASE_URL}/api/users/notifications`,

  // Admin endpoints
  ADMIN_ANALYTICS: `${API_BASE_URL}/api/admin/analytics/overview`,
  ADMIN_PAYOUTS: `${API_BASE_URL}/api/monetization/admin/payouts`,
  ADMIN_PAYOUT_PROCESS: id => `${API_BASE_URL}/api/monetization/admin/payouts/${id}/process`,

  // Boost endpoints (Legacy) / Viral Bounty (New)
  CREATE_BOOST: `${API_BASE_URL}/api/monetization/boost/create`,
  MONETIZATION_SUBSCRIPTION_STATUS: `${API_BASE_URL}/api/monetization/subscription/status`,
  CREATE_BOUNTY: `${API_BASE_URL}/api/revenue/create-bounty`,
  BOUNTY_BOARD: `${API_BASE_URL}/api/revenue/bounty-board`,
  MY_BOUNTIES: `${API_BASE_URL}/api/revenue/my-bounties`,
  CLAIM_BOUNTY: `${API_BASE_URL}/api/revenue/claim-bounty`,

  // TikTok endpoints
  TIKTOK_AUTH_START: `${API_BASE_URL}/api/tiktok/auth/start`,
  TIKTOK_STATUS: `${API_BASE_URL}/api/tiktok/status`,
  TIKTOK_CREATOR_INFO: `${API_BASE_URL}/api/tiktok/creator_info`,
  CONTENT_QUALITY_CHECK: `${API_BASE_URL}/api/content/quality-check`,

  // Facebook endpoints
  FACEBOOK_AUTH_START: `${API_BASE_URL}/api/facebook/auth/start`,
  FACEBOOK_STATUS: `${API_BASE_URL}/api/facebook/status`,
  FACEBOOK_UPLOAD: `${API_BASE_URL}/api/facebook/upload`,

  // YouTube endpoints
  YOUTUBE_AUTH_START: `${API_BASE_URL}/api/youtube/auth/start`,
  YOUTUBE_STATUS: `${API_BASE_URL}/api/youtube/status`,
  YOUTUBE_METADATA: `${API_BASE_URL}/api/youtube/metadata`,
  YOUTUBE_UPLOAD: `${API_BASE_URL}/api/youtube/upload`,

  // Instagram endpoints (via Facebook Graph)
  INSTAGRAM_STATUS: `${API_BASE_URL}/api/instagram/status`,
  INSTAGRAM_UPLOAD: `${API_BASE_URL}/api/instagram/upload`,

  // Twitter endpoints
  TWITTER_AUTH_START: `${API_BASE_URL}/api/twitter/oauth/start`,
  TWITTER_AUTH_PREPARE: `${API_BASE_URL}/api/twitter/oauth/prepare`,
  // OAuth1 prepare endpoint (for legacy uploads / native media)
  TWITTER_AUTH_PREPARE_OAUTH1: `${API_BASE_URL}/api/twitter/oauth1/prepare`,
  TWITTER_STATUS: `${API_BASE_URL}/api/twitter/connection/status`,
  TWITTER_TWEET_TEST: `${API_BASE_URL}/api/twitter/tweet/test`,
  TWITTER_TWEET_IMMEDIATE: `${API_BASE_URL}/api/twitter/tweet/immediate`,

  // Snapchat endpoints
  SNAPCHAT_AUTH_PREPARE: `${API_BASE_URL}/api/snapchat/oauth/prepare`,
  SNAPCHAT_STATUS: `${API_BASE_URL}/api/snapchat/status`,
  SNAPCHAT_METADATA: `${API_BASE_URL}/api/snapchat/metadata`,
  SNAPCHAT_CREATIVE: `${API_BASE_URL}/api/snapchat/creative`,
  SNAPCHAT_ANALYTICS: creativeId => `${API_BASE_URL}/api/snapchat/analytics/${creativeId}`,

  // Schedule actions (construct with ID)
  SCHEDULE_PAUSE: id => `${API_BASE_URL}/api/content/promotion-schedules/${id}/pause`,
  SCHEDULE_RESUME: id => `${API_BASE_URL}/api/content/promotion-schedules/${id}/resume`,
  SCHEDULE_RESCHEDULE: id => `${API_BASE_URL}/api/content/promotion-schedules/${id}/reschedule`,
  SCHEDULE_DELETE: id => `${API_BASE_URL}/api/content/promotion-schedules/${id}`,

  // Health check
  HEALTH: `${API_BASE_URL}/api/health`,

  // Credits (for AI/strategy credit balance)
  CREDITS_BALANCE: `${API_BASE_URL}/api/monetization/credits/balance`,

  // PayPal configuration endpoint used by frontend SDK loaders
  PAYMENTS_PAYPAL_CONFIG: `${API_BASE_URL}/api/payments/paypal/config`,

  // New platform endpoints (placeholders)
  SPOTIFY_AUTH_START: `${API_BASE_URL}/api/spotify/auth/start`,
  SPOTIFY_STATUS: `${API_BASE_URL}/api/spotify/status`,
  SPOTIFY_METADATA: `${API_BASE_URL}/api/spotify/metadata`,
  SPOTIFY_SEARCH: `${API_BASE_URL}/api/spotify/search`,
  SPOTIFY_PLAYLISTS: `${API_BASE_URL}/api/spotify/playlists`,

  REDDIT_AUTH_START: `${API_BASE_URL}/api/reddit/auth/start`,
  REDDIT_STATUS: `${API_BASE_URL}/api/reddit/status`,

  DISCORD_AUTH_START: `${API_BASE_URL}/api/discord/auth/start`,
  DISCORD_STATUS: `${API_BASE_URL}/api/discord/status`,
  DISCORD_METADATA: `${API_BASE_URL}/api/discord/metadata`,

  LINKEDIN_AUTH_START: `${API_BASE_URL}/api/linkedin/auth/start`,
  LINKEDIN_STATUS: `${API_BASE_URL}/api/linkedin/status`,
  LINKEDIN_METADATA: `${API_BASE_URL}/api/linkedin/metadata`,

  TELEGRAM_AUTH_START: `${API_BASE_URL}/api/telegram/auth/start`,
  TELEGRAM_AUTH_PREPARE: `${API_BASE_URL}/api/telegram/auth/prepare`,
  TELEGRAM_STATUS: `${API_BASE_URL}/api/telegram/status`,
  TELEGRAM_METADATA: `${API_BASE_URL}/api/telegram/metadata`,

  PINTEREST_AUTH_START: `${API_BASE_URL}/api/pinterest/auth/start`,
  PINTEREST_STATUS: `${API_BASE_URL}/api/pinterest/status`,
  PINTEREST_METADATA: `${API_BASE_URL}/api/pinterest/metadata`,
  PINTEREST_BOARDS: `${API_BASE_URL}/api/pinterest/boards`,

  // Monetization / Earnings
  EARNINGS_SUMMARY: `${API_BASE_URL}/api/monetization/earnings/summary`,
  EARNINGS_PAYOUT_SELF: `${API_BASE_URL}/api/monetization/earnings/payout/self`,
  EARNINGS_PAYOUTS: `${API_BASE_URL}/api/monetization/earnings/payouts`,
  EARNINGS_AGGREGATE_ADMIN: `${API_BASE_URL}/api/monetization/earnings/aggregate`,

  // Notifications (v2)
  NOTIFICATIONS_LIST: `${API_BASE_URL}/api/notifications`,
  NOTIFICATIONS_MARK_READ: `${API_BASE_URL}/api/notifications/read-all`,

  // Analytics endpoints
  ANALYTICS_USER: `${API_BASE_URL}/api/analytics/user`,
  ANALYTICS_WORKFLOW_SUMMARY: `${API_BASE_URL}/api/analytics/workflow-summary`,
  ANALYTICS_CLIP_SCANNER_CACHE: `${API_BASE_URL}/api/analytics/clip-scanner-cache`,

  // Support endpoints
  SUPPORT_TICKETS_SELF: `${API_BASE_URL}/api/admin/support/tickets/mine`,
  SUPPORT_TICKETS_CREATE: `${API_BASE_URL}/api/admin/support/tickets`,

  // Usage & Limits
  USAGE_STATS: `${API_BASE_URL}/api/usage/stats`,
  USAGE_UPGRADE: `${API_BASE_URL}/api/usage/upgrade`,
  USAGE_PRICING: `${API_BASE_URL}/api/usage/pricing`,

  // Platform aggregated status & user progress
  PLATFORM_STATUS: `${API_BASE_URL}/api/platform/status`,
  PLATFORM_DISCONNECT: platform => `${API_BASE_URL}/api/platform/disconnect/${platform}`,
  USER_PROGRESS: `${API_BASE_URL}/api/users/progress`,

  // Ads & Campaigns
  ADS: `${API_BASE_URL}/api/ads`,
  AD_LAUNCH: adId => `${API_BASE_URL}/api/ads/${adId}/launch`,
  AD_PAUSE: adId => `${API_BASE_URL}/api/ads/${adId}/pause`,
  AD_RESUME: adId => `${API_BASE_URL}/api/ads/${adId}/resume`,
  AD_ANALYTICS: adId => `${API_BASE_URL}/api/ads/${adId}/analytics`,

  // PayPal Subscriptions
  PAYPAL_SUBSCRIPTIONS: `${API_BASE_URL}/api/paypal-subscriptions`,
  PAYPAL_SUBSCRIPTION_STATUS: `${API_BASE_URL}/api/paypal-subscriptions/status`,
  PAYPAL_SUBSCRIPTION_USAGE: `${API_BASE_URL}/api/paypal-subscriptions/usage`,
  PAYPAL_CREATE_SUBSCRIPTION: `${API_BASE_URL}/api/paypal-subscriptions/create`,
  PAYPAL_CANCEL_SUBSCRIPTION: subscriptionId =>
    `${API_BASE_URL}/api/paypal-subscriptions/${subscriptionId}/cancel`,
};

// Firebase configuration
export const FIREBASE_CONFIG = {
  // This is loaded from .env files
};

// Feature flags
export const FEATURES = {
  ENABLE_STRIPE: false,
  ENABLE_ANALYTICS: true,
  DEBUG_MODE: process.env.NODE_ENV === "development",
};

// Default settings
export const DEFAULTS = {
  CONTENT_PER_PAGE: 10,
  AVATAR_URL: "https://cdn-icons-png.flaticon.com/512/3135/3135715.png",
};
